import {
    Alert,
    CircularProgress,
    Grid,
    IconButton,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { loadDataCatalog } from './document-generation-data-catalog.slice';
import { ContentCopy, Launch } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';

function DataCatalogPage() {
    const isLoading = useAppSelector((state) => state.documentGenerationDataCatalogSlice.isLoading);
    const dataCatalog = useAppSelector((state) => state.documentGenerationDataCatalogSlice.dataCatalog);

    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(loadDataCatalog());
    }, [dispatch]);

    return (
        <Grid container direction="row" size={12} spacing={2}>
            <Grid size={12}>
                <Typography variant="h4">{t('Data Catalog')}</Typography>
            </Grid>

            <Grid size={12}>
                <Alert severity="info">
                    <Stack direction="row" sx={{ alignItems: 'center', display: 'inline-flex' }}>
                        {t('See the documentation about how to build a Template using the data from the Data Catalog:')}
                        &nbsp;
                        <Launch />
                        <Link
                            href="https://www.notion.so/admikommunal/Document-generation-Template-guide-22942e7ea434807d8474ee903dd87bb6?source=copy_link"
                            target="_blank"
                            rel="noopener"
                            sx={{ ml: 0.5 }}
                        >
                            {t('Documentation Link')}
                        </Link>
                    </Stack>
                </Alert>
            </Grid>

            <Grid size={12}>
                {isLoading ? (
                    <CircularProgress />
                ) : (
                    <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <colgroup>
                            <col style={{ width: '30%' }} /> {/* Key */}
                            <col style={{ width: '10%' }} /> {/* Data Type */}
                            <col style={{ width: '60%' }} /> {/* Description */}
                        </colgroup>
                        <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: '#fbfcfe', zIndex: 1 }}>
                            <TableRow>
                                {['Key', 'Data Type', 'Description'].map((label) => (
                                    <TableCell
                                        key={label}
                                        sx={{
                                            fontWeight: 'bold',
                                            color: '#1e293b',
                                            py: 1,
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        {t(label)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...dataCatalog]
                                .sort((a, b) => a.key.localeCompare(b.key))
                                .map((item) => (
                                    <TableRow
                                        key={item.key}
                                        sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9fafb' } }}
                                    >
                                        <TableCell
                                            sx={{
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                maxWidth: 0, // enables shrinking within fixed table layout
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                                <Typography variant="body2">{item.key}</Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        if (navigator?.clipboard?.writeText) {
                                                            navigator.clipboard.writeText(item.key);
                                                        } else {
                                                            alert('Copy not supported in this environment.');
                                                        }
                                                    }}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>

                                        <TableCell sx={{ overflowWrap: 'break-word' }}>{item.dataType}</TableCell>

                                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                            <ReactMarkdown>{item.description}</ReactMarkdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                )}
            </Grid>
        </Grid>
    );
}

export default DataCatalogPage;
